import { useMemo } from "react";
import { Link } from "react-router-dom";

import { format, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { ArrowRight, BookOpen, Clock, Megaphone, RotateCcw, UsersRound } from "lucide-react";

import { ErrorState } from "@/shared/components/data/ErrorState";
import { StatCard } from "@/shared/components/data/StatCard";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { useAppSelector } from "@/store";
import { useGetSummaryQuery } from "@/store/apis/adminApi.enhanced";
import { selectAdmin } from "@/store/selectors";

/**
 * Dashboard — landing del backoffice.
 *
 * Composición:
 *   - Hero header con saludo + ambiente (hora local).
 *   - Botón "Actualizar" que forzea refetch con animación.
 *   - 3 StatCards de áreas (Advice, Popups, Circle) con click-through
 *     a sus features.
 *   - "Última actualización" en mono micro al pie — es un dashboard
 *     consumido por humanos, saber si los datos están frescos importa.
 */
export function DashboardPage() {
  const admin = useAppSelector(selectAdmin);
  const { data: summary, isLoading, isFetching, isError, error, refetch } = useGetSummaryQuery();

  const firstName = admin?.full_name.split(" ")[0] ?? "admin";
  const greeting = useMemo(() => getGreeting(), []);

  const stats = [
    {
      label: "Consejos",
      value: summary?.advice.active ?? 0,
      hint: summary
        ? `${summary.advice.total ?? 0} en total · ${summary.advice.categories ?? 0} categorías`
        : undefined,
      icon: <BookOpen className="h-4 w-4" />,
      to: "/consejos",
      kicker: "Activos",
    },
    {
      label: "Pop-ups",
      value: summary?.popups.live ?? 0,
      hint: summary
        ? `${summary.popups.scheduled ?? 0} programados · ${summary.popups.paused ?? 0} en pausa`
        : undefined,
      icon: <Megaphone className="h-4 w-4" />,
      to: "/popups",
      kicker: "En vivo",
    },
    {
      label: "Círculo",
      value: summary?.circle.groups_active ?? 0,
      hint: summary ? `${summary.circle.groups_archived ?? 0} archivados` : undefined,
      icon: <UsersRound className="h-4 w-4" />,
      to: "/circulo/grupos",
      kicker: "Grupos",
    },
  ];

  return (
    <>
      <PageHeader
        kicker={greeting}
        title={`Hola, ${firstName}`}
        description="Panorama rápido del ecosistema Arya. Entra a cada módulo para gestionar en detalle."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="gap-2"
          >
            <RotateCcw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
            {isFetching ? "Actualizando…" : "Actualizar"}
          </Button>
        }
      />

      {isError ? (
        <div className="overflow-hidden rounded-2xl border bg-card shadow-card-premium">
          <ErrorState
            error={error}
            onRetry={() => void refetch()}
            title="No pudimos cargar los indicadores"
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {stats.map((s) => (
            <Link
              key={s.to}
              to={s.to}
              aria-label={`Ir a ${s.label}`}
              className="group block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <StatCard
                label={s.label}
                value={
                  <span className="inline-flex items-baseline gap-2">
                    {s.value.toLocaleString("es-PE")}
                    <span className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                      {s.kicker}
                    </span>
                  </span>
                }
                hint={
                  <span className="inline-flex items-center gap-1.5">
                    {s.hint}
                    <ArrowRight className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                  </span>
                }
                icon={s.icon}
                loading={isLoading}
                className="transition-colors group-hover:border-primary/40"
              />
            </Link>
          ))}
        </div>
      )}

      {/* Timestamp del back */}
      {summary?.generated_at ? (
        <p className="mt-6 inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
          <Clock className="h-3 w-3" />
          Última actualización:{" "}
          <span
            className="font-medium text-foreground"
            title={format(new Date(summary.generated_at), "d 'de' MMMM, HH:mm", { locale: es })}
          >
            hace {formatDistanceToNow(new Date(summary.generated_at), { locale: es })}
          </span>
        </p>
      ) : null}
    </>
  );
}

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
}
